/**
 * Formatação das telas de e-mail.
 *
 * A regra que manda: **vazio não é zero**. `null`/`undefined` viram DASH — nunca `0`, `0%` ou
 * `R$ 0,00`. Um "0%" onde não houve envio afirma "0% de entrega", que é conclusão falsa.
 * Os valores chegam em BRL sem arredondar; arredonda-se só aqui, na exibição.
 */
import { MINUS, nf } from '@/components/v2/format';
import type { Metric } from './metrics';

export const DASH = '—';

export type Currency = 'BRL' | 'USD';

const finite = (v: number | null | undefined): v is number => typeof v === 'number' && Number.isFinite(v);

export interface Fmtr {
    /** "R$ 1.234,56" / "−R$ 12,00"; null → "—". */
    money: (v: number | null | undefined) => string;
    /** "606.249"; null → "—". */
    int: (v: number | null | undefined) => string;
    /** "94,7%"; null → "—" (denominador zero). */
    pct: (v: number | null | undefined) => string;
    /** Formata pelo tipo declarado da métrica. */
    metric: (met: Metric, v: number | null | undefined) => string;
    /** Rótulo da moeda em uso, para o cabeçalho. */
    currency: Currency;
}

/**
 * `dolar` é a cotação do envelope (`rates.dolar`). Em US$ os valores são convertidos para exibição;
 * sem cotação válida a conversão não acontece e a moeda volta a R$ — melhor R$ certo que US$ inventado.
 */
export const makeFmt = (currency: Currency, dolar: number | null | undefined): Fmtr => {
    const rate = currency === 'USD' && finite(dolar) && dolar > 0 ? dolar : 0;
    const money = (v: number | null | undefined) => {
        if (!finite(v)) return DASH;
        const value = rate ? v / rate : v;
        const sign = value < 0 ? MINUS : '';
        return sign + (rate ? 'US$ ' : 'R$ ') + nf(Math.abs(value), 2);
    };
    const int = (v: number | null | undefined) => (finite(v) ? Math.round(v).toLocaleString('pt-BR') : DASH);
    const pct = (v: number | null | undefined) => (finite(v) ? nf(v, 1) + '%' : DASH);
    return {
        money,
        int,
        pct,
        metric: (met, v) => (met.fmt === 'brl' ? money(v) : met.fmt === 'pct1' ? pct(v) : int(v)),
        currency: rate ? 'USD' : 'BRL',
    };
};

/** "1 – 27 out 2026" / "27 out 2026" quando é um dia só. */
const MON = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
export const periodLabel = (from: string, to: string) => {
    const [fy, fm, fd] = from.split('-').map(Number);
    const [ty, tm, td] = to.split('-').map(Number);
    if (from === to) return `${fd} ${MON[tm - 1]} ${ty}`;
    if (fy === ty && fm === tm) return `${fd} – ${td} ${MON[tm - 1]} ${ty}`;
    if (fy === ty) return `${fd} ${MON[fm - 1]} – ${td} ${MON[tm - 1]} ${ty}`;
    return `${fd} ${MON[fm - 1]} ${fy} – ${td} ${MON[tm - 1]} ${ty}`;
};

/** "2026-10-28T04:00:00+00:00" → "28/10, 04:00". `null` = nunca carregou. */
export const freshnessLabel = (iso: string | null): string => {
    if (!iso) return 'nunca carregada';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return 'nunca carregada';
    const p = (n: number) => String(n).padStart(2, '0');
    return `${p(d.getDate())}/${p(d.getMonth() + 1)}, ${p(d.getHours())}:${p(d.getMinutes())}`;
};

/** "gam:2026-10-27" → "27/10". Os dias sem carga do `flags.missing`. */
export const missingDayLabel = (entry: string) => {
    const iso = entry.includes(':') ? entry.slice(entry.indexOf(':') + 1) : entry;
    return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : entry;
};

/** Texto vazio da API vira "—": célula em branco parece defeito. */
export const text = (v: string | null | undefined, fallback = DASH) => (v && v.trim() ? v : fallback);
